import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/es";

import { Button, Card, Input, Select, Space, Typography, Row, Col, Skeleton } from "antd";
import { SearchOutlined } from "@ant-design/icons";

import { toast } from "../../components/toasts/toastStore";
import { PATH } from "../../redux/utils";
import { getPacientes, getAseguradorasCatalogo } from "./pacientesApi";
import "./PacientesPage.css";
import { usePermisos } from "../../context/PermisosContext";

dayjs.locale("es");

const { Title, Text, Paragraph } = Typography;

const BANNER_BG = "linear-gradient(135deg, var(--brand-dark, #3d5590) 0%, var(--brand-primary) 100%)";

const ORDEN_OPTIONS = [
  { value: "recientes", label: "Más recientes" },
  { value: "antiguos", label: "Más antiguos" },
  { value: "nombre_asc", label: "Nombre (A-Z)" },
  { value: "nombre_desc", label: "Nombre (Z-A)" },
  { value: "edad_asc", label: "Edad (menor a mayor)" },
  { value: "edad_desc", label: "Edad (mayor a menor)" },
];

const calcularEdad = (fechaNacimiento) => {
  if (!fechaNacimiento) return null;
  const d = dayjs(fechaNacimiento);
  if (!d.isValid()) return null;
  return dayjs().diff(d, "year");
};

export default function PacientesPage() {
  const navigate = useNavigate();
  const { perm } = usePermisos() || { perm: () => true };

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [telefono, setTelefono] = useState("");
  const [correo, setCorreo] = useState("");
  const [aseguradoras, setAseguradoras] = useState([]);
  const [orden, setOrden] = useState("recientes");
  const [textDebounced, setTextDebounced] = useState({ search: "", telefono: "", correo: "" });
  const [activeFilter, setActiveFilter] = useState("todos");
  const [aseguradorasCat, setAseguradorasCat] = useState([]);

  useEffect(() => {
    const t = setTimeout(() => setTextDebounced({ search, telefono, correo }), 350);
    return () => clearTimeout(t);
  }, [search, telefono, correo]);

  useEffect(() => {
    getAseguradorasCatalogo()
      .then((r) => setAseguradorasCat((r.data ?? []).map((a) => ({ label: a.nombre, value: a.id_aseguradora }))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    getPacientes({ ...textDebounced, aseguradoras, orden })
      .then((res) => setItems(res.data?.items ?? []))
      .catch(() => toast.error("No se pudieron cargar los pacientes"))
      .finally(() => setLoading(false));
  }, [textDebounced, aseguradoras, orden]);

  const counts = useMemo(() => ({
    total: items.length,
    conCita: items.filter((p) => Boolean(p.proxima_cita)).length,
    sinCita: items.filter((p) => !p.proxima_cita).length,
  }), [items]);

  const filteredItems = useMemo(() => {
    if (activeFilter === "con_cita") return items.filter((p) => Boolean(p.proxima_cita));
    if (activeFilter === "sin_cita") return items.filter((p) => !p.proxima_cita);
    return items;
  }, [items, activeFilter]);

  const exportarExcel = async () => {
    if (!filteredItems.length) {
      toast.error("No hay pacientes para exportar");
      return;
    }
    const XLSX = await import("xlsx");
    const header = ["Nombre", "Teléfono", "Correo", "Fecha de nacimiento", "Edad", "Dirección", "Próxima cita", "Estado"];
    const rows = filteredItems.map((p) => [
      p.nombre_completo,
      p.telefono,
      p.correo || "",
      p.fecha_nacimiento ? dayjs(p.fecha_nacimiento).format("DD/MM/YYYY") : "",
      calcularEdad(p.fecha_nacimiento) ?? "",
      p.direccion || "",
      p.proxima_cita ? dayjs(p.proxima_cita).format("DD/MM/YYYY HH:mm") : "",
      p.active === undefined || p.active ? "Activo" : "Inactivo",
    ]);
    const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
    ws["!cols"] = header.map((h, i) => ({
      wch: Math.min(40, Math.max(h.length, ...rows.map((r) => String(r[i] ?? "").length)) + 2),
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Pacientes");
    XLSX.writeFile(wb, `pacientes_${dayjs().format("YYYY-MM-DD")}.xlsx`);
  };

  const STAT_CARDS = [
    { key: "todos", label: "Total de pacientes", icon: <i className="fa-solid fa-user" />, iconCls: "paq-stat-icon-primario", count: counts.total },
    { key: "con_cita", label: "Con citas pendientes", icon: <i className="fa-solid fa-calendar-check" />, iconCls: "paq-stat-icon-correo", count: counts.conCita },
    { key: "sin_cita", label: "Sin cita programada", icon: <i className="fa-solid fa-calendar-xmark" />, iconCls: "paq-stat-icon-menores", count: counts.sinCita },
  ];

  return (
    <main className="paq-main">
      <div className="paq-content">
        <section className="paq-section">
          <div className="paq-filters-panel">
            <Space direction="vertical" size={2} style={{ marginBottom: 16 }}>
              <Title level={2} className="paq-title">Pacientes</Title>
              <Text className="paq-subtitle">Consulta y administra el expediente clinico de tus pacientes.</Text>
            </Space>

            <Row gutter={[16, 14]}>
              <Col xs={24} md={12} lg={6}>
                <div className="paq-field-label">Buscador</div>
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Nombre o teléfono..."
                  suffix={<SearchOutlined className="paq-input-suffix" />}
                  className="paq-control"
                  allowClear
                />
              </Col>
              <Col xs={24} md={12} lg={6}>
                <div className="paq-field-label">Teléfono</div>
                <Input value={telefono} onChange={(e) => setTelefono(e.target.value)} placeholder="Ej. 6532..." className="paq-control" allowClear />
              </Col>
              <Col xs={24} md={12} lg={6}>
                <div className="paq-field-label">Correo</div>
                <Input value={correo} onChange={(e) => setCorreo(e.target.value)} placeholder="Ej. paciente@correo.com" className="paq-control" allowClear />
              </Col>
              <Col xs={24} md={12} lg={6}>
                <div className="paq-field-label">Aseguradoras</div>
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  maxTagCount="responsive"
                  value={aseguradoras}
                  onChange={setAseguradoras}
                  options={aseguradorasCat}
                  placeholder="Todas las aseguradoras"
                  className="paq-control"
                />
              </Col>
            </Row>

            <Row gutter={[16, 14]} style={{ marginTop: 14 }} align="bottom">
              <Col xs={24} lg={6}>
                <div className="paq-actions">
                  <Button
                    className="paq-btn-clean"
                    onClick={() => {
                      setSearch("");
                      setTelefono("");
                      setCorreo("");
                      setAseguradoras([]);
                      setOrden("recientes");
                      setActiveFilter("todos");
                    }}
                  >
                    Limpiar filtros
                  </Button>
                </div>
              </Col>
            </Row>
          </div>

          <div className="paq-stats-row" style={{ gridTemplateColumns: `repeat(${STAT_CARDS.length}, minmax(0, 1fr))` }}>
            {STAT_CARDS.map(({ key, label, icon, iconCls, count }) => (
              <Card
                key={key}
                className={`paq-stat-card ${activeFilter === key ? "paq-stat-active" : ""}`}
                hoverable
                onClick={() => setActiveFilter((p) => (p === key ? "todos" : key))}
              >
                <Space align="center" size={10}>
                  <div className={`paq-stat-icon ${iconCls}`}>{icon}</div>
                  <div>
                    <div className="paq-stat-value">{count}</div>
                    <div className="paq-stat-label">{label}</div>
                  </div>
                </Space>
              </Card>
            ))}
          </div>

          <div className="paq-expedientes-card">
            <div className="paq-toolbar">
              <div className="paq-toolbar-left">
                <Title level={4} style={{ marginBottom: 0 }}>Pacientes ({filteredItems.length})</Title>
                <Text type="secondary">{filteredItems.length} pacientes encontrados</Text>
              </div>
              <div className="paq-toolbar-right">
                <Select
                  value={orden}
                  onChange={setOrden}
                  options={ORDEN_OPTIONS}
                  className="paq-orden-select"
                  suffixIcon={<i className="fa-solid fa-arrow-down-wide-short" />}
                />
                <Button className="paq-btn-clean paq-btn-export" icon={<i className="fa-solid fa-file-export" />} onClick={exportarExcel}>
                  Exportar
                </Button>
                {perm("pacientes", "insertar") && (
                  <Button
                  type="primary"
                  icon={<i className="fa-solid fa-plus" />}
                  onClick={() => navigate("/app/pacientes/nuevo")}
                  style={{ backgroundColor: "var(--brand-primary)", borderColor: "var(--brand-primary)" }}
                >
                  Nuevo paciente
                </Button>
                )}
              </div>
            </div>

            {!loading && filteredItems.length === 0 && (
              <div className="paq-empty">
                <div style={{ fontSize: 16, fontWeight: 500 }}>Sin pacientes que coincidan con los filtros</div>
              </div>
            )}

            <div className="paq-grid">
              {loading && Array.from({ length: 8 }).map((_, i) => (
                <div key={`sk-${i}`} className="paq-card paq-card-skeleton">
                  <div className="paq-card-header">
                    <Skeleton active title={{ width: "60%" }} paragraph={{ rows: 1, width: "40%" }} />
                  </div>
                  <div className="paq-card-body">
                    <Skeleton active title={false} paragraph={{ rows: 3 }} />
                  </div>
                </div>
              ))}

              {!loading && filteredItems.map((p) => {
                const edad = calcularEdad(p.fecha_nacimiento);
                const activo = p.active === undefined ? true : Boolean(p.active);
                return (
                  <div key={p.id_paciente} className="paq-card">
                    <div className="paq-card-banner" style={{ background: BANNER_BG }}>
                      {p.path && p.filename ? (
                        <img
                          className="paq-card-banner-img"
                          src={`${PATH.replace(/\/$/, "")}/${p.path}/${p.filename}`}
                          alt={p.nombre_completo}
                        />
                      ) : (
                        <i className="fa-solid fa-user paq-card-banner-icon" />
                      )}
                    </div>

                    <div className="paq-card-header">
                      <div className="paq-card-head">
                        <Paragraph className="paq-card-name" ellipsis={{ rows: 2 }}>
                          {p.nombre_completo}
                        </Paragraph>
                        <span className={`paq-tipo-badge ${activo ? "paq-tipo-activo" : "paq-tipo-inactivo"}`}>
                          {activo ? "Activo" : "Inactivo"}
                        </span>
                      </div>
                    </div>

                    <div className="paq-card-body">
                      <div className="paq-contenidos-box">
                        <i className="fa-solid fa-phone paq-info-icon" />
                        <div className="paq-info-text">
                          <span className="paq-info-label">Telefono</span>
                          <span className="paq-info-value">{p.telefono || "Sin registrar"}</span>
                        </div>
                      </div>
                      <div className="paq-contenidos-box">
                        <i className="fa-solid fa-cake-candles paq-info-icon" />
                        <div className="paq-info-text">
                          <span className="paq-info-label">Fecha de nacimiento</span>
                          <span className="paq-info-value">
                            {p.fecha_nacimiento ? dayjs(p.fecha_nacimiento).format("D MMM YYYY") : "—"}
                            {edad != null ? ` · ${edad} años` : ""}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="paq-card-footer">
                      {p.proxima_cita && (
                        <div className="paq-proxima-cita">
                          <i className="fa-solid fa-calendar-check" />
                          <span className="paq-proxima-cita-label">Próxima cita</span>
                          <span className="paq-proxima-cita-value">
                            {dayjs(p.proxima_cita).format("D MMM YYYY · h:mm A")}
                          </span>
                        </div>
                      )}
                      <Button
                        className="paq-btn-details"
                        icon={<i className="fa-solid fa-circle-info" />}
                        onClick={() => navigate(`/app/pacientes/${p.id_paciente}`)}
                      >
                        Ver expediente
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
